import * as React from 'react'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import employees from 'modules/employees/store/collections/employees'
import { locationActiveEmployees } from 'modules/locations/lib/locationEmployees'
import locations from 'modules/locations/store/collections/locations'
import { getSettingPageMeta } from 'modules/settings/lib/getSettingPageMeta'

const RootPage = React.lazy(() => import('modules/locations/pages/settings/RootPage'))

const AddEmployeeModalPage = React.lazy(
  () => import('modules/locations/pages/settings/AddEmployeeModalPage')
)
const AddHolidayModalPage = React.lazy(
  () => import('modules/locations/pages/settings/AddHolidayModalPage')
)
const AddWorkAreaModalPage = React.lazy(
  () => import('modules/locations/pages/settings/AddWorkAreaModalPage')
)
const AddWorkSpaceSidebarPage = React.lazy(
  () => import('modules/locations/pages/settings/AddWorkSpaceSidebarPage')
)
const AuthorizedEmployeesPage = React.lazy(
  () => import('modules/locations/pages/settings/AuthorizedEmployeesPage')
)
const AuthorizedHolidaysPage = React.lazy(
  () => import('modules/locations/pages/settings/AuthorizedHolidaysPage')
)
const EditHolidayModalPage = React.lazy(
  () => import('modules/locations/pages/settings/EditHolidayModalPage')
)
const EditWorkAreaModalPage = React.lazy(
  () => import('modules/locations/pages/settings/EditWorkAreaModalPage')
)
const ImportHolidaysModalPage = React.lazy(
  () => import('modules/locations/pages/settings/ImportHolidaysModalPage')
)
const LocationsPages = React.lazy(() => import('modules/locations/pages/Locations'))
const MoveEmployeeModalPage = React.lazy(
  () => import('modules/locations/pages/settings/MoveEmployeeModalPage')
)
const NewLocationPage = React.lazy(() => import('modules/locations/pages/settings/NewLocationPage'))

const ArchivedWorkAreasPage = React.lazy(
  () => import('modules/locations/components/ArchivedWorkAreas')
)

const ProtectedWorkAreasPage = React.lazy(
  () => import('modules/locations/pages/settings/ProtectedWorkAreasPage')
)
const RedirectToEmployeesPage = React.lazy(
  () => import('modules/locations/pages/settings/RedirectToEmployeesPage')
)
const RemoveLocationModalPage = React.lazy(
  () => import('modules/locations/pages/settings/RemoveLocationModalPage')
)

const AuthorizedWorkplaceDetailsPage = React.lazy(
  () => import('modules/locations/pages/settings/AuthorizedWorkplaceDetailsPage')
)

const LocationsContainer = React.lazy(
  () => import('modules/locations/components/LocationsContainer')
)

const WorkSpacesPage = React.lazy(() => import('modules/locations/pages/settings/WorkSpacesPage'))

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.locations.path,
      element: <RootPage />,
      handle: {
        meta: () => getSettingPageMeta(navigation, 'workplaces'),
      },
      children: [
        {
          index: true,
          element: <LocationsContainer />,
        },
        {
          path: navigation.settings.locations.removeId.path,
          element: <RemoveLocationModalPage />,
        },
        {
          path: navigation.settings.locations.locationNew.path,
          element: <NewLocationPage />,
          handle: {
            meta: () => ({
              title: i18n.t('settings.locations.add.title'),
            }),
          },
        },
        {
          path: navigation.settings.locations.locationsId.path,
          element: <LocationsPages />,
          handle: {
            meta: ({ params }) => {
              const { id } = params
              const location = locations.get(Number(id))

              if (!location) return

              const isHolidaysTab = window.location.pathname.split('/').includes('holidays')
              const employeesCount = locationActiveEmployees(location, employees)?.length ?? 0

              const headerTitle = `${location.attributes.name} - ${i18n.tp(
                'settings.locations.employees_count',
                {
                  count: employeesCount,
                }
              )}`
              return {
                title: isHolidaysTab ? undefined : headerTitle,
              }
            },
          },
          children: [
            {
              index: true,
              element: <RedirectToEmployeesPage />,
            },
            {
              path: navigation.settings.locations.locationsId.workAreasArchived.path,
              handle: {
                meta: () => ({
                  title: i18n.t('settings.locations.location.work_areas.archived.title'),
                  illustration: 'input-field',
                  description: i18n.t(
                    'settings.locations.location.work_areas.archived.description'
                  ),
                }),
              },
              element: <ArchivedWorkAreasPage />,
            },
            {
              path: `${navigation.settings.locations.locationsId.workAreas.path}/:workAreaId/work-spaces`, // TODO: refactor this when we move to Space Control
              element: <WorkSpacesPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('settings.locations.location.work_areas.manage_spaces'),
                }),
              },
              children: [
                {
                  path: 'add',
                  element: <AddWorkSpaceSidebarPage />,
                },
                {
                  path: ':workSpaceId/edit',
                  element: <AddWorkSpaceSidebarPage />,
                },
              ],
            },
            {
              path: navigation.settings.locations.locationsId.details.path,
              element: <AuthorizedWorkplaceDetailsPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('settings.locations.tabs.details'),
                }),
              },
            },
            {
              path: navigation.settings.locations.locationsId.employees.path,
              element: <AuthorizedEmployeesPage />,
              handle: {
                meta: ({ params }) => {
                  const { id } = params
                  const location = locations.get(Number(id))

                  if (!location) return

                  return {
                    title: i18n.t('settings.locations.tabs.default_employees'),
                    illustration: 'group',
                    description: i18n.t(
                      'settings.locations.location.employees.default_description',
                      {
                        name: location.attributes.name,
                      }
                    ),
                  }
                },
              },
              children: [
                {
                  path: navigation.settings.locations.locationsId.employees.add.path,
                  element: <AddEmployeeModalPage />,
                },
                {
                  path: navigation.settings.locations.locationsId.employees.moveEmployeeId.path,
                  element: <MoveEmployeeModalPage />,
                },
              ],
            },
            {
              path: navigation.settings.locations.locationsId.holidays.path,
              element: <AuthorizedHolidaysPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('settings.locations.location.holidays.title'),
                  description: i18n.t('settings.locations.location.holidays.description'),
                }),
              },
              children: [
                {
                  path: navigation.settings.locations.locationsId.holidays.add.path,
                  element: <AddHolidayModalPage />,
                },
                {
                  path: navigation.settings.locations.locationsId.holidays.editHolidayId.path,
                  element: <EditHolidayModalPage />,
                },
                {
                  path: navigation.settings.locations.locationsId.holidays.import.path,
                  element: <ImportHolidaysModalPage />,
                },
              ],
            },
            {
              path: navigation.settings.locations.locationsId.workAreas.path,
              element: <ProtectedWorkAreasPage />,
              handle: {
                meta: () => ({
                  title: i18n.t('settings.locations.location.work_areas.title'),
                  illustration: 'input-field',
                  description: i18n.t('settings.locations.location.work_areas.description'),
                }),
              },
              children: [
                {
                  path: navigation.settings.locations.locationsId.workAreas.add.path,
                  element: <AddWorkAreaModalPage />,
                },
                {
                  path: navigation.settings.locations.locationsId.workAreas.workAreaIdEdit.path,
                  element: <EditWorkAreaModalPage />,
                },
                {
                  path: navigation.settings.locations.locationsId.workAreas.addWorkSpace.path,
                  element: <AddWorkSpaceSidebarPage />,
                },
                {
                  path: navigation.settings.locations.locationsId.workAreas.editWorkSpace.path,
                  element: <AddWorkSpaceSidebarPage />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]
}

export default settingsRoutes
